/* ==========================================================================
   Dynamic Article Store (Unified Seed & API Data)
   Provides reactive, multilingual access to News, Case Studies & Product Updates
   with automatic fallback to bundled static data when offline.
   ========================================================================== */

import scrapedData from '../../scraped_case_studies_full.json';
import { articleIndexTranslations } from '../locales/articleTranslationsData.js';
import { articleFullTranslations } from '../locales/articleFullTranslations.js';
import { getLocale } from '../locales/i18n.js';

// Internal Store Map
const articlesMap = new Map();
const subscribers = new Set();
let isInitialized = false;

/**
 * Initialize Store with bundled static data
 */
function seedStaticArticles() {
  const languages = ['cs', 'en', 'de', 'es', 'pt', 'ar', 'it'];

  Object.entries(scrapedData || {}).forEach(([slug, raw]) => {
    if (!raw || !slug) return;

    const normalizedSlug = slug.replace('#', '').replace('/case-studies/', '').replace('/latest-news/', '').replace('/product-updates/', '').replace('/detection/', '').replace(/\/$/, '') || slug;
    
    // Build translations object from articleTranslationsData & articleFullTranslations
    const translations = {};
    for (const lang of languages) {
      const idx = (articleIndexTranslations[lang] && articleIndexTranslations[lang][normalizedSlug]) || {};
      const full = (articleFullTranslations[lang] && articleFullTranslations[lang][normalizedSlug]) || {};
      
      translations[lang] = {
        title: full.title || idx.title || raw.title || '',
        excerpt: full.excerpt || idx.excerpt || raw.excerpt || '',
        category: full.category || idx.category || raw.category || '',
        content: full.content || raw.content || '',
        date: full.date || idx.date || raw.date || ''
      };
    }

    let articleImg = raw.img || '';
    if (normalizedSlug === 'sed-kiss-integration-automated-alerts') {
      const currentLang = getLocale() || 'en';
      articleImg = currentLang === 'cs' ? '/images/propojení SED a KISS_CZ.png' : '/images/SED_a_KISS.png';
    }

    const articleObj = {
      slug: normalizedSlug,
      category: (raw.category || 'latest-news').toLowerCase(),
      date: raw.date || '',
      tag: raw.tag || '',
      publisher: raw.publisher || 'JALUD Embedded',
      img: articleImg,
      yt: raw.yt || '',
      videos: raw.videos || (raw.yt ? [raw.yt] : []),
      translations,
      crossReferences: raw.crossReferences || [],
      relatedArticles: raw.relatedArticles || []
    };

    articlesMap.set(normalizedSlug, articleObj);
  });
}

/**
 * Asynchronously fetch latest articles from REST API and merge into store
 */
export async function syncArticlesFromApi() {
  try {
    const res = await fetch('/api/articles');
    if (!res.ok) throw new Error(`API returned status ${res.status}`);
    const data = await res.json();
    
    if (data && Array.isArray(data.articles)) {
      data.articles.forEach(art => {
        if (art && art.slug) {
          articlesMap.set(art.slug, {
            ...articlesMap.get(art.slug),
            ...art
          });
        }
      });
      notifySubscribers();
    }
  } catch (err) {
    // Graceful offline fallback - store already contains seeded static articles
    console.debug('[ArticleStore] API sync skipped or offline, using static store:', err.message);
  }
}

/**
 * Initialize store once
 */
export function initArticleStore() {
  if (isInitialized) return;
  isInitialized = true;
  seedStaticArticles();
  syncArticlesFromApi();
}

/**
 * Subscribe to store mutations
 */
export function subscribeArticleStore(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function notifySubscribers() {
  subscribers.forEach(fn => {
    try { fn(); } catch (e) { console.error('[ArticleStore] subscriber error:', e); }
  });
}

/**
 * Get all articles sorted by date descending
 */
export function getAllArticles() {
  if (!isInitialized) initArticleStore();
  const list = Array.from(articlesMap.values());
  list.sort((a, b) => {
    const timeA = a.date ? new Date(a.date).getTime() : 0;
    const timeB = b.date ? new Date(b.date).getTime() : 0;
    return timeB - timeA;
  });
  return list;
}

/**
 * Get articles filtered by category
 */
export function getArticlesByCategory(category) {
  const all = getAllArticles();
  if (!category) return all;
  const catLower = category.toLowerCase();
  return all.filter(a => {
    const c = (a.category || '').toLowerCase();
    if (catLower === 'latest-news' || catLower === 'news') {
      return c === 'latest-news' || c === 'news';
    }
    return c === catLower;
  });
}

/**
 * Get single article by slug with localized fields
 */
export function getArticleBySlug(slugOrId, targetLang) {
  if (!isInitialized) initArticleStore();
  const lang = targetLang || getLocale() || 'cs';
  const cleanId = (slugOrId || '').replace('#', '').replace('/case-studies/', '').replace('/latest-news/', '').replace('/product-updates/', '').replace('/detection/', '').replace(/\/$/, '') || slugOrId;

  const raw = articlesMap.get(cleanId) || articlesMap.get(slugOrId);
  if (!raw) return null;

  const trans = (raw.translations && raw.translations[lang]) || (raw.translations && raw.translations.cs) || (raw.translations && raw.translations.en) || {};

  let displayImg = raw.img;
  if (cleanId === 'sed-kiss-integration-automated-alerts') {
    displayImg = lang === 'cs' ? '/images/propojení SED a KISS_CZ.png' : '/images/SED_a_KISS.png';
  }

  return {
    ...raw,
    img: displayImg,
    title: trans.title || raw.title || '',
    excerpt: trans.excerpt || raw.excerpt || '',
    categoryDisplay: trans.category || (raw.category || '').replace('-', ' ').toUpperCase(),
    content: trans.content || raw.content || '',
    date: trans.date || raw.date || '',
    activeLang: lang
  };
}

// Auto-initialize on module load
initArticleStore();
